// hooks/useSCMData.js — transaction-based data processing
// Records are computed from transaction collections; the existing
// {weekEnding, fields:{}} shape is preserved so all display components
// (Dashboard, AnalyticsPage, ReportsPage) work unchanged.
import { useState, useEffect, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/msalService";
import {
  fetchSCMRecords,
  createSCMRecord,
  updateSCMRecord,
  deleteSCMRecord,
} from "../services/sharepointService";
import { SEED_TRANSACTIONS } from "../data/seedTransactions";
import { computeWeeklyRecords, getDetailRecords, getDetailFromSP } from "../utils/computeMetrics";

// Convert SharePoint flat record to our shape.
// FieldData is a JSON blob that holds aggregate fields + optionally
// a _transactions key containing the raw individual records for that week.
function spToRecord(spFields) {
  const { id, ID, Week_Ending, FieldData, ...rest } = spFields;
  let fields = {};
  let weekTransactions = null;
  if (FieldData) {
    try {
      const parsed = JSON.parse(FieldData);
      const { _transactions, ...aggFields } = parsed;
      fields = aggFields;
      weekTransactions = _transactions || null;
    } catch {}
  } else {
    fields = rest;
  }
  return { id: id || ID, weekEnding: Week_Ending, fields, _transactions: weekTransactions };
}

// Build the SharePoint payload for a weekly record.
// Optionally embeds raw transaction rows so drill-down works after reload.
function recordToSP(record, weekTransactions = null) {
  const payload = { ...(record.fields || {}) };
  if (weekTransactions && Object.keys(weekTransactions).length > 0) {
    payload._transactions = weekTransactions;
  }
  return {
    Week_Ending: record.weekEnding,
    FieldData: JSON.stringify(payload),
  };
}

// ─── Natural keys for deduplication — defines what makes a record "the same" ──
// If an uploaded record matches an existing one on these fields, it overwrites it.
const NATURAL_KEYS = {
  contracts:                  (t) => `${normalizeDate(t.weekEnding)}|${String(t.title).trim().toLowerCase()}|${String(t.vendor).trim().toLowerCase()}`,
  purchaseOrders:             (t) => t.poNumber ? String(t.poNumber).trim() : `${normalizeDate(t.weekEnding)}|${String(t.vendor).trim().toLowerCase()}|${String(t.description).trim().toLowerCase()}`,
  cscMemos:                   (t) => `${normalizeDate(t.date || t.weekEnding)}|${String(t.title).trim().toLowerCase()}`,
  tenderBoardItems:           (t) => t.tbNumber ? String(t.tbNumber).trim() : `${normalizeDate(t.date || t.weekEnding)}|${String(t.title).trim().toLowerCase()}`,
  suppliers:                  (t) => String(t.name).trim().toLowerCase(),
  rfxItems:                   (t) => `${normalizeDate(t.weekEnding)}|${String(t.title).trim().toLowerCase()}`,
  stakeholderEngagements:     (t) => `${normalizeDate(t.date || t.weekEnding)}|${String(t.description).trim().toLowerCase()}`,
  supplierPerformanceReviews: (t) => `${normalizeDate(t.weekEnding)}|${String(t.supplierName).trim().toLowerCase()}|${String(t.reviewType).trim().toLowerCase()}`,
  dieselRecords:              (t) => `${normalizeDate(t.weekEnding)}|${String(t.location || 'uyo base').trim().toLowerCase()}`,
  logisticsRecords:           (t) => `${normalizeDate(t.weekEnding)}|${String(t.route || '').trim().toLowerCase()}`,
  projectUpdates:             (t) => `${normalizeDate(t.weekEnding)}|${String(t.key).trim()}`,
  processNotes:               (t) => normalizeDate(t.weekEnding),
  costSavings:                (t) => `${normalizeDate(t.date || t.weekEnding)}|${String(t.description || "").trim().toLowerCase()}|${String(t.supplier || "").trim().toLowerCase()}`,
};

const normalizeDate = (d) => {
  if (!d) return "";
  const s = String(d).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // Use local date parts to avoid UTC-shift (new Date(s).toISOString() shifts by TZ offset)
  const parsed = new Date(s);
  if (!isNaN(parsed)) {
    const y  = parsed.getFullYear();
    const mo = String(parsed.getMonth() + 1).padStart(2, "0");
    const dy = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  }
  return s;
};

const STORAGE_KEY = "lekoil_scm_transactions";

function loadStoredTransactions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        // Back-fill any seed collection that is completely absent from stored data
        // (e.g. costSavings added after user's first load). Does not overwrite
        // collections the user already has data for.
        const backfilled = { ...parsed };
        let changed = false;
        for (const key of Object.keys(SEED_TRANSACTIONS)) {
          if (!backfilled[key]) {
            backfilled[key] = SEED_TRANSACTIONS[key];
            changed = true;
          }
        }
        if (changed) saveTransactions(backfilled);
        return { data: backfilled, isSeed: false };
      }
    }
  } catch {}
  return { data: SEED_TRANSACTIONS, isSeed: true };
}

function saveTransactions(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

export function useSCMData() {
  const { instance, accounts } = useMsal();

  // --- Transaction state (source of truth in demo/upload mode; cleared in live SP mode) ---
  // Initialise from localStorage so uploaded data survives page refreshes.
  const [transactions, setTransactions] = useState(() => loadStoredTransactions().data);
  const [isSeedData, setIsSeedData] = useState(() => loadStoredTransactions().isSeed);

  // --- SharePoint fallback records (used only in live SP mode) ---
  const [spRecords, setSpRecords] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(true);

  // --- Derive weekly records from transactions (demo mode) ---
  const computedRecords = useMemo(
    () => computeWeeklyRecords(transactions),
    [transactions]
  );

  // Use computed records in demo mode, SP records in live mode
  const records = isDemoMode ? computedRecords : (spRecords || computedRecords);

  // --- Load from SharePoint ---
  const loadRecords = useCallback(async () => {
    if (!accounts?.length) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessToken(instance, accounts);
      const raw = await fetchSCMRecords(token);
      const mapped = raw.map(spToRecord);
      setSpRecords(mapped);
      setIsDemoMode(false);
      // Reconstruct individual transaction records from embedded data in each SP week
      const reconstructed = {};
      for (const rec of mapped) {
        if (rec._transactions) {
          for (const [key, arr] of Object.entries(rec._transactions)) {
            if (Array.isArray(arr)) {
              reconstructed[key] = [...(reconstructed[key] || []), ...arr];
            }
          }
        }
      }
      if (Object.keys(reconstructed).length > 0) {
        setTransactions(reconstructed);
        setIsSeedData(false);
      } else {
        // No embedded transactions yet — clear seed data so drill-down is honest
        setTransactions((prev) => isSeedData ? {} : prev);
      }
    } catch (err) {
      console.warn("Using demo/transaction data:", err.message);
      setIsDemoMode(true);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, isSeedData]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  // Persist real (non-seed) transactions to localStorage whenever they change
  useEffect(() => {
    if (!isSeedData) saveTransactions(transactions);
  }, [transactions, isSeedData]);

  // ─── CRUD (still works against SP in live mode; mutates transactions in demo) ───

  const addRecord = useCallback(async (record) => {
    if (isDemoMode) {
      // In demo mode this is a no-op since records are computed from transactions.
      // Transactions are added via bulkAdd (upload) or individual transaction adders.
      return { ...record, id: String(Date.now()) };
    }
    const token = await getAccessToken(instance, accounts);
    await createSCMRecord(token, recordToSP(record));
    await loadRecords();
  }, [isDemoMode, instance, accounts, loadRecords]);

  const editRecord = useCallback(async (id, record) => {
    if (isDemoMode) {
      // No-op for computed records — edit the underlying transactions instead.
      return;
    }
    const token = await getAccessToken(instance, accounts);
    await updateSCMRecord(token, id, recordToSP(record));
    await loadRecords();
  }, [isDemoMode, instance, accounts, loadRecords]);

  const removeRecord = useCallback(async (id) => {
    if (isDemoMode) {
      // Remove all transactions for this weekEnding
      const rec = computedRecords.find((r) => r.id === id);
      if (rec) {
        const week = rec.weekEnding;
        setTransactions((prev) => {
          const out = {};
          for (const [key, arr] of Object.entries(prev)) {
            out[key] = Array.isArray(arr) ? arr.filter((t) => t.weekEnding !== week) : arr;
          }
          return out;
        });
      }
      return;
    }
    const token = await getAccessToken(instance, accounts);
    await deleteSCMRecord(token, id);
    await loadRecords();
  }, [isDemoMode, instance, accounts, loadRecords, computedRecords]);

  // ─── Bulk upload — upsert by natural key so re-uploads overwrite, not duplicate ─
  const bulkAdd = useCallback(async (incomingTransactions) => {
    // incomingTransactions is { contracts[], purchaseOrders[], ... }
    if (isDemoMode) {
      // Compute the merged state synchronously from current transactions,
      // then set once — avoids unsafe closure-over-loop-variable lint warning.
      let created = 0, updated = 0;
      const merged = { ...transactions };
      for (const [key, arr] of Object.entries(incomingTransactions)) {
        if (!Array.isArray(arr) || !arr.length) continue;
        const getNKey = NATURAL_KEYS[key] || ((t) => t.id);
        const existing = [...(transactions[key] || [])];
        let i = 0;
        for (const t of arr) {
          const normDate = normalizeDate(t.weekEnding);
          const item = { ...t, weekEnding: normDate, id: t.id || `upload-${key}-${Date.now()}-${i}` };
          const nkey = getNKey(item);
          const idx = existing.findIndex((e) => getNKey(e) === nkey);
          if (idx >= 0) {
            existing[idx] = { ...item, id: existing[idx].id }; // preserve original id
            updated++;
          } else {
            existing.push(item);
            created++;
          }
          i++;
        }
        merged[key] = existing;
      }
      setIsSeedData(false); // mark as real data so the useEffect will persist it
      setTransactions(merged);
      return { success: true, created, updated };
    }

    // Live SP mode: save weekly aggregates + embed raw transactions in FieldData
    const token = await getAccessToken(instance, accounts);
    const weeklyRecords = computeWeeklyRecords(incomingTransactions);

    // Group the incoming transactions by weekEnding so each SP record carries its own rows
    const byWeek = {};
    for (const [key, arr] of Object.entries(incomingTransactions)) {
      if (!Array.isArray(arr)) continue;
      for (const t of arr) {
        const week = normalizeDate(t.weekEnding);
        if (!week) continue;
        if (!byWeek[week]) byWeek[week] = {};
        if (!byWeek[week][key]) byWeek[week][key] = [];
        byWeek[week][key].push(t);
      }
    }

    for (const rec of weeklyRecords) {
      const weekTx = byWeek[rec.weekEnding] || null;
      const existing = records.find((r) => normalizeDate(r.weekEnding) === normalizeDate(rec.weekEnding));
      if (existing?.id) {
        await updateSCMRecord(token, existing.id, recordToSP(rec, weekTx));
      } else {
        await createSCMRecord(token, recordToSP(rec, weekTx));
      }
    }
    await loadRecords();
    return { success: true };
  }, [isDemoMode, instance, accounts, loadRecords, records, transactions]);

  // ─── Detail resolver — called by Dashboard when a KPI card is clicked ────────
  // In live SP mode: show weekly aggregate rows from SharePoint.
  // In demo/upload mode: show individual transaction records.
  const getDetail = useCallback((metricKey, weekEnding = null) => {
    // Prefer individual transaction records when available (uploaded via Excel)
    if (!isSeedData && Object.keys(transactions).length > 0) {
      return getDetailRecords(transactions, metricKey, weekEnding);
    }
    // Fall back to SP weekly aggregates when no transaction-level data exists
    if (!isDemoMode && spRecords?.length) {
      return getDetailFromSP(spRecords, metricKey);
    }
    return getDetailRecords(transactions, metricKey, weekEnding);
  }, [isSeedData, isDemoMode, spRecords, transactions]);

  // ─── Direct transaction mutators ─────────────────────────────────────────────
  const addTransaction = useCallback((collectionKey, item) => {
    setTransactions((prev) => ({
      ...prev,
      [collectionKey]: [{ ...item, id: item.id || `t-${Date.now()}` }, ...(prev[collectionKey] || [])],
    }));
  }, []);

  const editTransaction = useCallback((collectionKey, id, updatedItem) => {
    setTransactions((prev) => ({
      ...prev,
      [collectionKey]: (prev[collectionKey] || []).map((t) =>
        t.id === id ? { ...t, ...updatedItem } : t
      ),
    }));
  }, []);

  const deleteTransaction = useCallback((collectionKey, id) => {
    setTransactions((prev) => ({
      ...prev,
      [collectionKey]: (prev[collectionKey] || []).filter((t) => t.id !== id),
    }));
  }, []);

  return {
    records,
    transactions,
    loading,
    error,
    isDemoMode,
    refresh: loadRecords,
    addRecord,
    editRecord,
    removeRecord,
    bulkAdd,
    getDetail,
    addTransaction,
    editTransaction,
    deleteTransaction,
  };
}
